import { useState, useContext, useEffect, useMemo, useRef } from "react";

import { i18n } from "../../../../../translate/i18n";

import { TenantContext } from "../../../../../templates/context/TenantContext";
import { useForecastStore } from "../../../../../viewModel/stores/forecastStore";

import { getMonthName, sumColumn } from "../../../../../utils/utils";

import { AccuracyRows } from "./AccuracyRows";

import {
  EmptyTableRow,
  LoadingContent,
  TableControls,
} from "../../../../../components/SharedComponents/sharedComponents.index";

import { 
  ColumnTitlesRow, 
  ColumnLabelCell,
  MonthSortByButton,
  TableColumnTitle
} from "../../../sharedComponents/analysisModuleSharedComponents.index";

import { Text } from "@aidron/aidron-ds";
import PropTypes from 'prop-types';
import { LOADING_CONTENT_TYPE } from "../../../../../components/SharedComponents/LoadingContent/loading.content.constants";
import { useAnalysisViewModel } from "../../../../../viewModel/useAnalysysViewModel";

const { t } = i18n;

const calculateDiffPercent = (values) => {
  const sumIndexOne = sumColumn(values.selectedPredictions, values.indexOne);
  const sumIndexTwo = sumColumn(values.selectedPredictions, values.indexTwo);

  if (sumIndexOne && sumIndexTwo) {
    return ((sumIndexOne - sumIndexTwo) / sumIndexTwo) * 100;
  } else {
    return null;
  }
}

const calculateParticipation = (values) => {
  const allPredictions = sumColumn(values.allPredictions, values.name);
  const selectedPredictions = sumColumn(values.selectedPredictions, values.name);
  return (selectedPredictions / allPredictions) * 100;
}

const accuracyColumnsPast = () => {
  return [
    {
      label: t("currentPlan"),
      name: "officialClientPrediction",
      type: "demand",
      calculate: values => sumColumn(values.selectedPredictions, values.name),
    },
    {
      label: t("ai"),
      name: "officialAidronPrediction",
      fullLabel: t("aiForecast"),
      type: "prediction",
      calculate: values => sumColumn(values.selectedPredictions, values.name),
    },
    {
      label: t("realized"),
      name: "realized",
      fullLabel: t("realizedSales"),
      type: "realized",
      calculate: values => sumColumn(values.selectedPredictions, values.name),
    },
    {
      label: t("partPercent"),
      name: "participationInRealized",
      fullLabel: t("percentParticipationOnRealized"),
      type: "%",
      calculate: values => calculateParticipation(values),
    },
    {
      label: t("percentDifferenceAIVsRealized"),
      name: "percentOfficialAidronVsRealized",
      indexOne: "officialAidronPrediction",
      indexTwo: "realized",
      type: "dif%",
      calculate: values => calculateDiffPercent(values),
    },
    {
      label: t("percentDifferenceCurrentPlanVsRealized"),
      name: "percentOfficialClientVsRealized",
      indexOne: "officialClientPrediction",
      indexTwo: "realized",
      type: "dif",
      calculate: values => calculateDiffPercent(values),
    },
  ];
};

const columnsLength = accuracyColumnsPast().length;

function AccuracyColumnLabelsRow({ actions, ctx, uiState }) {

  const { setSortRowsBy, setHierarchyCellHeight } = actions || {};
  const { forecastAssertivenessMonths, defineHierarchyToDisplay, pastPeriodColumns } = ctx || {};
  const { 
    sortRowsBy,
    freezeColumns,
    monthHeaderCellHeight,
    sumPreviousColumnsWidth,
    evaluateLastCategoryColumn,    
  } = uiState || {};

  const { hierarchy } = useContext(TenantContext);
  const hierarchyLabelCellRef = useRef(null);

  const cellRightBorder = (index) => {
    return !freezeColumns && index === defineHierarchyToDisplay().length - 1
      ? "cell-right-border"
      : "";
  };

  useEffect(() => {
    if (setHierarchyCellHeight) {
      setHierarchyCellHeight(
        hierarchyLabelCellRef.current?.getBoundingClientRect().height
      );
    }
  }, []);

  return (
    <tr>
      <td
        className={`background-color-white padding-sides-030rem z-5 position-sticky cell-bottom-border ${freezeColumns ? "start-0" : "" }`}
        style={{ top: `${monthHeaderCellHeight}px` }}
      />

      {/* absorvido do HierarchyHeaderCells */}
      {defineHierarchyToDisplay().map((level, index) => (
        <th
          rowSpan="1"
          key={`${level}`}
          className={`category-th cell-bottom-border subtitle-cell z-5 background-color-white vertical-align-middle width-12px-on-icon height-12px-on-icon 
            ${evaluateLastCategoryColumn(index)} 
            ${cellRightBorder(index)}`}
          style={{
            position: freezeColumns ? "sticky" : "",
            top: `${monthHeaderCellHeight}px`,
            left: freezeColumns ? `${sumPreviousColumnsWidth(index + 1)}px` : "",
          }}
          ref={index === 0 ? hierarchyLabelCellRef : null}
        >
          <div className="row">
            <div className="col d-flex align-items-center small-arrow-button">
              <Text>{i18n.t(`${hierarchy.labels[level]}`)}</Text>
            </div>
          </div>
        </th>
      ))}
      {/* fim absorvido do HierarchyHeaderCells */}

      {forecastAssertivenessMonths.map((month, index) => (
        <>
          {pastPeriodColumns.map((column, i) => (
            <ColumnLabelCell
              uiState={{
                column: column,
                upperCellsHeight: monthHeaderCellHeight,
                cellLeftBorder: index !== 0 && i === 0,
                renderSortByButton: 
                  <MonthSortByButton
                    actions={{ setSortRowsBy: setSortRowsBy }}
                    ctx={{ monthObj: { ...month, months: [month.month] } }}
                    uiState={{ column: column, sortRowsBy: sortRowsBy }}/>
              }}
              key={`${month.forecastId}-${month.month}-${column.label}`}
            />
          ))}
        </>
      ))}
    </tr>
  );
};

export function AccuracyTable({ actions, ctx, uiState }) {

  const { setCheckedProducts } = actions || {}

  const { checkedProducts = [], selectedHierarchyIndexes = [], selectedProductIds = [], products = [] } = ctx || {};

  const { freezeColumns } = uiState || {}

  const { hierarchy } = useContext(TenantContext);

  const initialSortBy = useForecastStore((s) => s.initialSortBy);

  const vm = useAnalysisViewModel({
    shouldFetchLastSixMonthsPredictions: true
  })

  const [checkColumnWidth, setCheckColumnWidth] = useState(0);
  const [hierarchyColumnsWidth, setHierarchyColumnsWidth] = useState([]);
  const [monthHeaderCellHeight, setMonthHeaderCellHeight] = useState(0);
  const [hierarchyCellHeight, setHierarchyCellHeight] = useState(0);

  const [sortRowsBy, setSortRowsBy] = useState(initialSortBy);

  const headerHeight = monthHeaderCellHeight + hierarchyCellHeight;

  const defineHierarchyToDisplay = () => hierarchy.levels.filter((level, index) => selectedHierarchyIndexes.includes(index));
  const evaluateLastCategoryColumn = (index) => freezeColumns && index === defineHierarchyToDisplay().length - 1 ? "right-border-fade" : "";

  const sumPreviousColumnsWidth = (currentColumnIndex, columnsWidth) => {
    const previousColumnsWidth = columnsWidth ?? [checkColumnWidth].concat(hierarchyColumnsWidth);
    let sum = 0;
    for (let i = 0; i < currentColumnIndex; i++) {
      sum += previousColumnsWidth[i];
    }
    return sum;
  }

  const generateColumnTitle = (data) => {
    if (data.label) return data.label;
    return `${getMonthName(data.month.toString())} ${data.year}`;
  };

  const pastPeriodColumns = useMemo(() => accuracyColumnsPast(), []);

  if(vm.uiState.isLoadingFetchLast6MonthsPredictions) {
    return (
      <LoadingContent label={"Carregando suas predições..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>
    );
  }

  return (
    <TableControls
      freezeColumns={freezeColumns}
      columns={vm.ctx.forecastAssertivenessMonths}
      accuracyTableScrollBehavior={true}>
        
      {vm.ctx.predictionsForAssertivenessAnalysis?.length > 0 && vm.ctx.forecastAssertivenessMonths?.length > 0 ? (
        <table id="download-table" className="table table-striped table-hover padding-070rem-on-th-and-td">
          <thead>
            <ColumnTitlesRow
              actions={{ setMonthHeaderCellHeight: setMonthHeaderCellHeight }}
              ctx={{
                monthColumns: vm.ctx.forecastAssertivenessMonths,
                defineHierarchyToDisplay: defineHierarchyToDisplay,
              }}
              uiState={{
                freezeColumns: freezeColumns,
                monthHeaderCellHeight: monthHeaderCellHeight,
                monthHeaderCellsSpan: columnsLength,
                accuracyTable: true,
                renderColumnTitleLabel: (data) => ( <TableColumnTitle label={generateColumnTitle(data)} /> )
              }}/>

            <AccuracyColumnLabelsRow
              actions={{
                setSortRowsBy: setSortRowsBy,
                setHierarchyCellHeight: setHierarchyCellHeight
              }}
              ctx={{
                forecastAssertivenessMonths: vm.ctx.forecastAssertivenessMonths,
                defineHierarchyToDisplay: defineHierarchyToDisplay,
                pastPeriodColumns: pastPeriodColumns
              }}
              uiState={{
                freezeColumns: freezeColumns,
                sumPreviousColumnsWidth: sumPreviousColumnsWidth,
                evaluateLastCategoryColumn: evaluateLastCategoryColumn,
                monthHeaderCellHeight: monthHeaderCellHeight,
                sortRowsBy: sortRowsBy,
              }}/>

          </thead>

          <tbody>
            <AccuracyRows
              actions={{
                setCheckColumnWidth: setCheckColumnWidth,
                setHierarchyColumnsWidth: setHierarchyColumnsWidth,
                setCheckedProducts: setCheckedProducts
              }}
              ctx={{
                selectedProductIds: selectedProductIds,
                forecastAssertivenessMonths: vm.ctx.forecastAssertivenessMonths || [],
                selectedHierarchyIndexes: selectedHierarchyIndexes,
                predictionsForAssertivenessAnalysis: vm.ctx.predictionsForAssertivenessAnalysis || [],
                pastPeriodColumns: pastPeriodColumns,
                products: products
              }}
              uiState={{
                freezeColumns: freezeColumns,
                sumPreviousColumnsWidth: sumPreviousColumnsWidth,
                checkedProducts: checkedProducts,
                evaluateLastCategoryColumn: evaluateLastCategoryColumn,
                headerHeight: headerHeight,
                sortRowsBy: sortRowsBy
              }}/>

          </tbody>            
        </table>
      ) : (
        <EmptyTableRow 
          ctx={{
            labels: {
              emptyTableMessage: t("emptyResult")
            }
          }}
          uiState={{
            accuracyTable: true
          }}/>
      )}

    </TableControls>
  );
};

AccuracyColumnLabelsRow.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};

AccuracyTable.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};